import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import SEOHead from '../../components/base/SEOHead';
import { supabase } from '../../lib/supabase';

interface Subscription {
  id: string;
  plan: string;
  status: 'active' | 'expired' | 'cancelled';
  start_date: string;
  end_date: string;
  amount: number;
}

export default function RobotPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'pricing' | 'performance'>('overview');
  const [pepPrice, setPepPrice] = useState(0.003);

  const handleViewRobots = () => {
    const robotsSection = document.getElementById('robots-section');
    if (robotsSection) {
      robotsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleGetStarted = () => {
    setActiveTab('pricing');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const scrollToPricing = () => {
    setActiveTab('pricing');
    // Scroll to pricing section after state update
    setTimeout(() => {
      const pricingSection = document.getElementById('pricing-section');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const scrollToPlans = () => {
    const plansSection = document.getElementById('robot-plans');
    if (plansSection) {
      plansSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const plans = [
    {
      name: 'Starter',
      price: '6,667',
      period: 'month',
      usdEquivalent: '$20',
      features: [
        'Automated arbitrage trading',
        'Up to 10,000 PEP capital limit',
        'Basic market analysis',
        '24/7 trading execution',
        'Email support',
        'Weekly performance reports'
      ],
      popular: false,
      maxCapital: 10000,
      id: 'starter',
      badge: 'Best for Beginners',
      badgeColor: 'bg-gradient-to-r from-blue-500 to-cyan-500',
      gradient: 'from-blue-500 to-cyan-500',
      limits: 'Capital Limit: 10,000 PEP'
    },
    {
      name: 'Basic',
      price: '33,333',
      period: 'month',
      usdEquivalent: '$100',
      features: [
        'Advanced arbitrage strategies',
        'Up to 50,000 PEP capital limit',
        'Real-time market analysis',
        'Multi-exchange trading',
        'Priority email support',
        'Daily performance reports',
        'Basic risk management tools'
      ],
      popular: false,
      maxCapital: 50000,
      id: 'basic',
      badge: null,
      badgeColor: '',
      gradient: 'from-purple-500 to-pink-500',
      limits: 'Capital Limit: 50,000 PEP'
    },
    {
      name: 'Professional',
      price: '166,667',
      period: 'month',
      usdEquivalent: '$500',
      features: [
        'Premium arbitrage algorithms',
        'Up to 500,000 PEP capital limit',
        'AI-powered market predictions',
        'Advanced multi-exchange trading',
        'Priority support (24/7)',
        'Real-time performance analytics',
        'Advanced risk management',
        'Custom trading strategies'
      ],
      popular: true,
      maxCapital: 500000,
      id: 'professional',
      badge: 'Most Popular',
      badgeColor: 'bg-gradient-to-r from-purple-500 to-pink-500',
      gradient: 'from-purple-500 to-pink-500',
      limits: 'Capital Limit: 500,000 PEP'
    },
    {
      name: 'Enterprise',
      price: '333,333',
      period: 'month',
      usdEquivalent: '$1,000',
      features: [
        'Elite arbitrage algorithms',
        'Unlimited capital - No restrictions',
        'Full AI-powered automation',
        'Institutional-grade strategies',
        'Dedicated account manager',
        'Real-time alerts & notifications',
        'Advanced analytics dashboard',
        'API access & custom integrations',
        'White-glove support'
      ],
      popular: false,
      maxCapital: null,
      id: 'enterprise',
      badge: 'Unlimited',
      badgeColor: 'bg-gradient-to-r from-yellow-500 to-orange-500',
      gradient: 'from-yellow-500 to-orange-500',
      limits: 'Unlimited Capital'
    }
  ];

  const stats = [
    { label: 'Win Rate', value: '94.7%', icon: 'ri-trophy-line', color: 'text-green-400' },
    { label: 'Avg Daily Return', value: '3.2%', icon: 'ri-line-chart-line', color: 'text-blue-400' },
    { label: 'Assets Managed', value: '$2.4M+', icon: 'ri-money-dollar-circle-line', color: 'text-purple-400' },
    { label: 'Active Users', value: '1,247', icon: 'ri-group-line', color: 'text-pink-400' }
  ];

  const features = [
    {
      icon: 'ri-brain-line',
      title: 'Advanced AI',
      description: 'Neural networks trained on millions of trades'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Risk Management',
      description: 'Built-in stop-loss and position sizing'
    },
    {
      icon: 'ri-time-line',
      title: '24/7 Trading',
      description: 'Never miss a profitable opportunity'
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Lightning Fast',
      description: 'Execute trades in milliseconds'
    },
    {
      icon: 'ri-bar-chart-box-line',
      title: 'Real-Time Analytics',
      description: 'Track performance with live dashboards'
    },
    {
      icon: 'ri-lock-line',
      title: 'Secure & Safe',
      description: 'Bank-level encryption and security'
    }
  ];

  const performanceData = [
    { month: 'Jan', return: 12.4 },
    { month: 'Feb', return: 15.8 },
    { month: 'Mar', return: 18.2 },
    { month: 'Apr', return: 14.6 },
    { month: 'May', return: 21.3 },
    { month: 'Jun', return: 19.7 }
  ];

  useEffect(() => {
    checkUser();
    fetchPepPrice();
  }, []);

  const fetchPepPrice = async () => {
    try {
      const { data } = await supabase
        .from('price_history')
        .select('price')
        .eq('symbol', 'PEP')
        .order('timestamp', { ascending: false })
        .limit(1)
        .single();
      
      if (data?.price) {
        setPepPrice(data.price);
      }
    } catch (error) {
      console.error('Error fetching PepCoin price:', error);
    }
  };

  const checkUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);

      if (user) {
        await fetchSubscription(user.id);
      }
    } catch (error) {
      console.error('Error checking user:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscription = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', userId)
        .eq('type', 'robot_subscription')
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (data && !error) {
        const endDate = new Date(data.created_at);
        endDate.setMonth(endDate.getMonth() + 1);
        
        const isActive = new Date() < endDate;
        
        setSubscription({
          id: data.id,
          plan: data.description || 'Unknown',
          status: isActive ? 'active' : 'expired',
          start_date: data.created_at,
          end_date: endDate.toISOString(),
          amount: data.amount
        });
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const handleSubscribe = async (plan: typeof plans[0]) => {
    if (!user) {
      navigate('/auth/signin');
      return;
    }

    setSelectedPlan(plan.id);
    setShowPaymentModal(true);
  };

  const processPayment = async () => {
    if (!user || !selectedPlan) return;

    const plan = plans.find(p => p.id === selectedPlan);
    if (!plan) return;

    try {
      setLoading(true);

      // ✅ Check ALL possible PepCoin balance sources
      console.log('🔍 Checking PepCoin balance from all sources...');

      // Source 1: profiles table
      const { data: profile } = await supabase
        .from('profiles')
        .select('pepcoin_balance')
        .eq('id', user.id)
        .single();

      // Source 2: user_wallets table
      const { data: wallet } = await supabase
        .from('user_wallets')
        .select('total_balance, available_balance')
        .eq('user_id', user.id)
        .maybeSingle();

      // Source 3: user_assets table (PEP asset)
      const { data: pepAsset } = await supabase
        .from('user_assets')
        .select('balance, available_balance')
        .eq('user_id', user.id)
        .eq('symbol', 'PEP')
        .maybeSingle();

      // Calculate total PepCoin from all sources
      const profileBalance = parseFloat(String(profile?.pepcoin_balance || 0));
      const walletBalance = parseFloat(String(wallet?.available_balance || wallet?.total_balance || 0));
      const assetBalance = parseFloat(String(pepAsset?.available_balance || pepAsset?.balance || 0));

      console.log('💰 Balance sources:', {
        profiles: profileBalance,
        wallet: walletBalance,
        assets: assetBalance
      });

      // Use the highest balance found (this is your actual PepCoin)
      const totalPepCoin = Math.max(profileBalance, walletBalance, assetBalance);

      console.log(`✅ Total PepCoin available: ${totalPepCoin.toLocaleString()} PEP`);
      console.log(`💳 Plan cost: ${plan.price.toLocaleString()} PEP`);

      if (totalPepCoin < parseFloat(String(plan.price))) {
        alert(`❌ Insufficient PepCoin balance!\n\nYou have: ${totalPepCoin.toLocaleString()} PEP\nRequired: ${plan.price.toLocaleString()} PEP\n\nPlease deposit more PepCoin to your wallet.`);
        setLoading(false);
        return;
      }

      // Deduct from the source with the highest balance
      if (assetBalance >= parseFloat(String(plan.price))) {
        // Deduct from user_assets
        const newBalance = assetBalance - parseFloat(String(plan.price));
        await supabase
          .from('user_assets')
          .update({ 
            balance: newBalance,
            available_balance: newBalance
          })
          .eq('user_id', user.id)
          .eq('symbol', 'PEP');
        
        console.log('✅ Deducted from user_assets');
      } else if (walletBalance >= parseFloat(String(plan.price))) {
        // Deduct from user_wallets
        const newBalance = walletBalance - parseFloat(String(plan.price));
        await supabase
          .from('user_wallets')
          .update({ 
            total_balance: newBalance,
            available_balance: newBalance
          })
          .eq('user_id', user.id);
        
        console.log('✅ Deducted from user_wallets');
      } else {
        // Deduct from profiles
        const newBalance = profileBalance - parseFloat(String(plan.price));
        await supabase
          .from('profiles')
          .update({ pepcoin_balance: newBalance })
          .eq('id', user.id);
        
        console.log('✅ Deducted from profiles');
      }

      // Record transaction
      const { error: txError } = await supabase
        .from('transactions')
        .insert({
          user_id: user.id,
          type: 'robot_subscription',
          amount: parseFloat(String(plan.price)),
          currency: 'PEP',
          status: 'completed',
          description: `${plan.name} Plan - AI Trading Robot`,
          metadata: { plan_id: plan.id, plan_name: plan.name }
        });

      if (txError) throw txError;

      await fetchSubscription(user.id);
      setShowPaymentModal(false);
      setSelectedPlan(null);
      alert(`🎉 Success! ${plan.name} Plan activated!\n\nYour AI Trading Robot is now active and ready to trade.`);
    } catch (error) {
      console.error('❌ Payment error:', error);
      alert('Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEOHead
        title="AI Trading Robot - Automated Crypto Trading | PepCoin"
        description="Advanced AI-powered trading robots for automated cryptocurrency trading. Choose from Basic, Pro, or VIP plans with 24/7 automated trading, risk management, and real-time analytics."
        keywords="AI trading robot, automated crypto trading, trading bot, cryptocurrency automation, PepCoin robot"
      />
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 text-white">
        <Navbar />

        {/* Back Button */}
        <div className="pt-20 px-4">
          <div className="max-w-7xl mx-auto">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/15 border border-white/20 rounded-lg text-white transition-all duration-300 cursor-pointer group mb-4"
            >
              <i className="ri-arrow-left-line text-lg group-hover:-translate-x-1 transition-transform"></i>
              <span className="text-sm font-medium">Back</span>
            </button>
          </div>
        </div>

        {/* Hero Section */}
        <div className="relative pt-12 pb-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-pink-600/20"></div>
          <div className="absolute inset-0">
            <div className="absolute top-20 left-20 w-72 h-72 bg-purple-500/30 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-pink-500/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl sm:rounded-3xl mb-6 sm:mb-8 shadow-2xl shadow-purple-500/50 animate-bounce">
                <i className="ri-robot-2-line text-3xl sm:text-4xl md:text-5xl text-white"></i>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight px-2">
                Elite AI Trading
                <span className="block bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  System
                </span>
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 mb-6 sm:mb-8 max-w-3xl mx-auto px-4">
                Advanced algorithms. 24/7 automation. Maximum returns.
              </p>

              {subscription?.status === 'active' ? (
                <div className="inline-flex flex-col sm:flex-row items-center gap-3 px-4 sm:px-6 md:px-8 py-3 sm:py-4 bg-green-500/20 border border-green-500/50 rounded-xl sm:rounded-2xl">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-sm sm:text-base text-green-400 font-semibold">Active: {subscription.plan}</span>
                  </div>
                  <button
                    onClick={() => navigate('/trading')}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-green-500 hover:bg-green-600 text-white text-sm sm:text-base rounded-lg sm:rounded-xl font-semibold transition-all whitespace-nowrap"
                  >
                    Go to Trading
                  </button>
                </div>
              ) : (
                <button
                  onClick={scrollToPlans}
                  className="px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-sm sm:text-base md:text-lg font-bold rounded-xl sm:rounded-2xl shadow-2xl shadow-purple-500/50 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  Get Started Now
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              )}
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mt-12 sm:mt-16 md:mt-20">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 text-center hover:bg-white/10 transition-all transform hover:scale-105"
                >
                  <i className={`${stat.icon} text-2xl sm:text-3xl md:text-4xl ${stat.color} mb-2 sm:mb-3`}></i>
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-gray-400 text-xs sm:text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 sm:mb-12">
          <div className="flex justify-center gap-2 sm:gap-3 md:gap-4">
            {(['overview', 'pricing', 'performance'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 sm:px-6 md:px-8 py-2 sm:py-3 rounded-lg sm:rounded-xl text-sm sm:text-base font-semibold transition-all whitespace-nowrap ${
                  activeTab === tab
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 sm:pb-16 md:pb-20">
            {/* Features Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-12 sm:mb-16 md:mb-20">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 hover:bg-white/10 transition-all transform hover:scale-105"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-5 md:mb-6">
                    <i className={`${feature.icon} text-2xl sm:text-2xl md:text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-2 sm:mb-3">{feature.title}</h3>
                  <p className="text-sm sm:text-base text-gray-400 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>

            {/* How It Works */}
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white text-center mb-8 sm:mb-10 md:mb-12">How It Works</h2>
              <div className="grid sm:grid-cols-3 gap-6 sm:gap-6 md:gap-8">
                {[
                  { step: '1', title: 'Choose Plan', desc: 'Select the perfect plan for your trading goals', icon: 'ri-checkbox-circle-line' },
                  { step: '2', title: 'Activate Robot', desc: 'Configure your strategy and start automated trading', icon: 'ri-settings-3-line' },
                  { step: '3', title: 'Earn Profits', desc: 'Watch your portfolio grow with AI-powered trades', icon: 'ri-money-dollar-circle-line' }
                ].map((item, index) => (
                  <div key={index} className="text-center">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-5 md:mb-6">
                      <i className={`${item.icon} text-2xl sm:text-3xl md:text-4xl text-white`}></i>
                    </div>
                    <div className="text-purple-400 text-sm sm:text-base font-bold mb-2">Step {item.step}</div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-2 sm:mb-3">{item.title}</h3>
                    <p className="text-sm sm:text-base text-gray-400">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Pricing Tab */}
        {activeTab === 'pricing' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 sm:pb-16 md:pb-20">
            <div className="text-center mb-8 sm:mb-10 md:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">Choose Your Plan</h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-400">All plans paid with PepCoin</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className={`relative bg-white/5 backdrop-blur-xl border rounded-2xl sm:rounded-3xl p-5 sm:p-6 md:p-8 hover:bg-white/10 transition-all transform hover:scale-105 ${
                    plan.popular ? 'border-purple-500 shadow-2xl shadow-purple-500/50' : 'border-white/10'
                  }`}
                >
                  {plan.badge && (
                    <div className={`absolute -top-3 sm:-top-4 left-1/2 -translate-x-1/2 ${plan.badgeColor} px-3 sm:px-4 md:px-6 py-1 sm:py-1.5 md:py-2 rounded-full text-white text-xs sm:text-sm font-bold whitespace-nowrap`}>
                      {plan.badge}
                    </div>
                  )}

                  <div className="text-center mb-6 sm:mb-7 md:mb-8">
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-3 sm:mb-4">{plan.name}</h3>
                    <div className="flex items-baseline justify-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                      <span className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                        {plan.price} PEP
                      </span>
                      <span className="text-sm sm:text-base text-gray-400">/mo</span>
                    </div>
                    <div className="text-sm sm:text-base text-cyan-400 font-semibold mb-1">{plan.usdEquivalent} USD</div>
                    <div className="text-xs sm:text-sm text-gray-500">{plan.limits}</div>
                  </div>

                  <ul className="space-y-2 sm:space-y-3 md:space-y-4 mb-6 sm:mb-7 md:mb-8">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 sm:gap-3 text-sm sm:text-base text-gray-300">
                        <i className="ri-check-line text-green-400 text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleSubscribe(plan)}
                    disabled={subscription?.status === 'active'}
                    className={`w-full py-3 sm:py-3.5 md:py-4 rounded-lg sm:rounded-xl text-sm sm:text-base font-bold transition-all whitespace-nowrap ${
                      subscription?.status === 'active'
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : `bg-gradient-to-r ${plan.gradient} hover:shadow-2xl text-white`
                    }`}
                  >
                    {subscription?.status === 'active' ? 'Subscribed' : 'Subscribe'}
                  </button>
                </div>
              ))}
            </div>

            {/* Payment Info */}
            <div className="mt-10 sm:mt-12 md:mt-16 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-2xl sm:rounded-3xl p-5 sm:p-6 md:p-8">
              <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-5 md:mb-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-coin-line text-2xl sm:text-2xl md:text-3xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white">Pay with PepCoin</h3>
                  <p className="text-sm sm:text-base text-gray-400">Fast, secure, and zero fees</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="flex items-center gap-2 sm:gap-3 text-sm sm:text-base text-gray-300">
                  <i className="ri-check-line text-green-400 text-lg sm:text-xl flex-shrink-0"></i>
                  <span>Instant activation</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 text-sm sm:text-base text-gray-300">
                  <i className="ri-check-line text-green-400 text-lg sm:text-xl flex-shrink-0"></i>
                  <span>No transaction fees</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 text-sm sm:text-base text-gray-300">
                  <i className="ri-check-line text-green-400 text-lg sm:text-xl flex-shrink-0"></i>
                  <span>Cancel anytime</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 text-sm sm:text-base text-gray-300">
                  <i className="ri-check-line text-green-400 text-lg sm:text-xl flex-shrink-0"></i>
                  <span>30-day money back</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Performance Tab */}
        {activeTab === 'performance' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 sm:pb-16 md:pb-20">
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl sm:rounded-3xl p-5 sm:p-8 md:p-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white text-center mb-8 sm:mb-10 md:mb-12">6-Month Performance</h2>
              
              {/* Performance Chart */}
              <div className="mb-8 sm:mb-10 md:mb-12">
                <div className="flex items-end justify-between gap-2 sm:gap-3 md:gap-4 h-48 sm:h-64 md:h-80">
                  {performanceData.map((data, index) => (
                    <div key={index} className="flex-1 flex flex-col items-center gap-2 sm:gap-3 md:gap-4">
                      <div className="text-sm sm:text-lg md:text-2xl font-bold text-green-400">+{data.return}%</div>
                      <div
                        className="w-full bg-gradient-to-t from-purple-500 to-pink-500 rounded-t-lg sm:rounded-t-xl transition-all hover:opacity-80"
                        style={{ height: `${(data.return / 25) * 100}%` }}
                      ></div>
                      <div className="text-xs sm:text-sm md:text-base text-gray-400 font-semibold">{data.month}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Performance Stats */}
              <div className="grid sm:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                <div className="text-center p-4 sm:p-5 md:p-6 bg-white/5 rounded-xl sm:rounded-2xl">
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-green-400 mb-1 sm:mb-2">+96.8%</div>
                  <div className="text-xs sm:text-sm md:text-base text-gray-400">Total Return (6 months)</div>
                </div>
                <div className="text-center p-4 sm:p-5 md:p-6 bg-white/5 rounded-xl sm:rounded-2xl">
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-400 mb-1 sm:mb-2">16.1%</div>
                  <div className="text-xs sm:text-sm md:text-base text-gray-400">Avg Monthly Return</div>
                </div>
                <div className="text-center p-4 sm:p-5 md:p-6 bg-white/5 rounded-xl sm:rounded-2xl">
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-purple-400 mb-1 sm:mb-2">0.53%</div>
                  <div className="text-xs sm:text-sm md:text-base text-gray-400">Avg Daily Return</div>
                </div>
              </div>

              {/* Testimonials */}
              <div className="mt-8 sm:mt-10 md:mt-12 grid sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
                {[
                  { name: 'Sarah Chen', role: 'Professional Trader', text: 'Best trading robot I\'ve ever used. Consistent profits every month!', rating: 5 },
                  { name: 'Michael Rodriguez', role: 'Crypto Investor', text: 'The AI is incredibly smart. It adapts to market conditions perfectly.', rating: 5 }
                ].map((testimonial, index) => (
                  <div key={index} className="bg-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6">
                    <div className="flex gap-1 mb-3 sm:mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-yellow-400 text-base sm:text-lg md:text-xl"></i>
                      ))}
                    </div>
                    <p className="text-sm sm:text-base text-gray-300 mb-3 sm:mb-4 italic">"{testimonial.text}"</p>
                    <div>
                      <div className="text-sm sm:text-base font-bold text-white">{testimonial.name}</div>
                      <div className="text-xs sm:text-sm text-gray-400">{testimonial.role}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Payment Modal */}
        {showPaymentModal && selectedPlan && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-white/10 rounded-2xl sm:rounded-3xl p-5 sm:p-6 md:p-8 max-w-md w-full">
              <div className="flex items-center justify-between mb-5 sm:mb-6">
                <h3 className="text-xl sm:text-2xl font-bold text-white">Confirm Subscription</h3>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center hover:bg-white/10 rounded-lg sm:rounded-xl transition-all"
                >
                  <i className="ri-close-line text-xl sm:text-2xl text-gray-400"></i>
                </button>
              </div>

              {(() => {
                const plan = plans.find(p => p.id === selectedPlan);
                const pepCoinsNeeded = Math.ceil(plan?.price ? plan.price / pepPrice : 0);
                return plan ? (
                  <>
                    <div className="bg-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 mb-5 sm:mb-6">
                      <div className="flex items-center justify-between mb-3 sm:mb-4">
                        <span className="text-sm sm:text-base text-gray-400">Plan</span>
                        <span className="text-sm sm:text-base text-white font-bold">{plan.name}</span>
                      </div>
                      <div className="flex items-center justify-between mb-3 sm:mb-4">
                        <span className="text-sm sm:text-base text-gray-400">Duration</span>
                        <span className="text-sm sm:text-base text-white font-bold">1 Month</span>
                      </div>
                      <div className="flex items-center justify-between mb-3 sm:mb-4">
                        <span className="text-sm sm:text-base text-gray-400">Price</span>
                        <span className="text-sm sm:text-base text-white font-bold">{plan.price} PEP</span>
                      </div>
                      <div className="flex items-center justify-between mb-3 sm:mb-4">
                        <span className="text-sm sm:text-base text-gray-400">PEP Rate</span>
                        <span className="text-sm sm:text-base text-gray-300">${pepPrice.toFixed(4)}/PEP</span>
                      </div>
                      <div className="border-t border-white/10 pt-3 sm:pt-4 flex items-center justify-between">
                        <span className="text-sm sm:text-base text-gray-400">Total</span>
                        <div className="text-right">
                          <span className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent block">
                            {plan.price.toLocaleString()} PEP
                          </span>
                          <span className="text-xs sm:text-sm text-gray-400">≈ ${(plan.price * pepPrice).toFixed(2)} USD</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-5 sm:mb-6">
                      <div className="flex gap-2 sm:gap-3">
                        <i className="ri-information-line text-yellow-400 text-lg sm:text-xl flex-shrink-0"></i>
                        <p className="text-yellow-200 text-xs sm:text-sm">
                          You need {plan.price.toLocaleString()} PepCoin (≈${(plan.price * pepPrice).toFixed(2)} USD) in your wallet. Make sure you have sufficient funds.
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={processPayment}
                      disabled={loading}
                      className="w-full py-3 sm:py-3.5 md:py-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-sm sm:text-base font-bold rounded-lg sm:rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>
                          Processing...
                        </span>
                      ) : (
                        'Confirm Payment'
                      )}
                    </button>
                  </>
                ) : null;
              })()}
            </div>
          </div>
        )}

        {/* Robot Plans Section */}
        <div id="robot-plans" className="py-20 px-4">
          {/* ... existing code ... */}
        </div>
      </div>
    </>
  );
}
